/** @prettier */

import {Grid, Box, Stack} from '@mui/material';
import {useState, useEffect, Dispatch, SetStateAction} from 'react';
import {Auth} from 'aws-amplify';
import {useNavigate} from 'react-router-dom';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import LoginFooter from '../../component/LoginFooter';
import {PrimaryButton} from '../../component/BasicButton';
//Style import
import '../../css/common/common.css';

import {routeList} from '../PrivateRoute';
import {format} from '../../define';

/**
 * パスワードリセット
 * @returns JSX
 */
const ResetPassword = () => {
  const [pageFlag, setPageFlag] = useState(true); //検証コード送信前フォームと検証コード送信後フォーム切り替えフラグ
  const [emailAddr, setEmailAddr] = useState(''); //メールアドレス

  return (
    <Grid container className="loginMain">
      {/* 登録フォーム側 */}
      <Grid item xs={12} md={6} className="loginMain__leftFrame">
        <Stack className="loginMain__leftFrame">
          <Box className="loginMain__leftFrame__input">
            {/* フラグで検証コード送信前フォームと検証コード送信後フォームを切り替える */}
            {pageFlag ? (
              <BeforeSendCodeForm
                emailAddr={emailAddr}
                setEmailAddr={setEmailAddr}
                setPageFlag={setPageFlag}
              />
            ) : (
              <AfterSendCodeForm emailAddr={emailAddr} />
            )}
          </Box>
          <LoginFooter />
        </Stack>
      </Grid>
      {/* 画像表示側 */}
      <Grid
        item
        xs={0}
        md={6}
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/img/mysite/login/login.webp)`,
        }}
        className="loginMain__backgroundImageIn"></Grid>
    </Grid>
  );
};

/**
 * 検証コード送信前フォーム
 * - emailAddr メールアドレス
 * - setEmailAddr メールアドレス設定関数
 * - setPageFlag 検証コード送信前フォームと検証コード送信後フォーム切り替えフラグ
 * @returns JSX
 */
const BeforeSendCodeForm = (props: {
  emailAddr: string;
  setEmailAddr: Dispatch<SetStateAction<string>>;
  setPageFlag: Dispatch<SetStateAction<boolean>>;
}) => {
  const navigate = useNavigate();
  const [reqPwdResetBtnDisabled, setReqPwdResetBtnDisabled] = useState(true); //REQUEST PASSWORD RESETボタン有効フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [emailAddrError, setEmailAddrError] = useState(false); //メールアドレス

  //フォーマット正常フラグ
  const [emailCheck, setEmailCheck] = useState(true); //メールアドレス

  //入力項目のチェック
  useEffect(() => {
    if (
      props.emailAddr === '' || //TextBoxにデータが入力されているかチェック
      emailAddrError || //TextBoxの入力規則違反に対するエラー判定
      apiWait //API処理中判定
    ) {
      setReqPwdResetBtnDisabled(true);
    } else {
      setReqPwdResetBtnDisabled(false);
    }
  }, [props.emailAddr, emailAddrError, apiWait]);

  /**
   * 検証コード送信前フォーム入力チェック
   * @returns 結果
   */
  const reqPwdResetFormDataCheck = () => {
    setEmailCheck(true);
    if (props.emailAddr.search(/.+@.+/) === -1) {
      setEmailCheck(false);
      return false;
    }
    return true;
  };

  /**
   * REQUEST PASSWORD RESETボタン押下時処理
   */
  const onReqPwdReset = async () => {
    //メールアドレスの形式が一致する場合に検証コード送信し次の入力項目を表示
    //正しくない場合はエラーメッセージを表示
    setAPIWait(true);
    if (reqPwdResetFormDataCheck()) {
      //コード送信、ページ内遷移してフォームを切り替える。
      await Auth.forgotPassword(props.emailAddr)
        .then(data => {
          alert(
            'A verification code has been sent to your email address. \nPlease note that the temporary password issued from M-ACCESS can not be reset. \nPlease log in with the temporary password.'
          );
          props.setPageFlag(false);
        })
        .catch(err => {
          alert('ForgotPassword Failure!');
        });
    }
    setAPIWait(false);
  };

  /**
   * BACKボタン押下時処理
   */
  const backButton = () => {
    //一つ前のページに戻る
    navigate(-1);
  };

  return (
    <Stack spacing={2} className="loginMain__leftFrame__input__contents">
      <h1>Reset your password</h1>
      <h2>Enter your e-mail address to reset your password.</h2>
      {/* 入力されたメールアドレスの形式が一致しない場合は、警告文を表示 */}
      {emailCheck === false ? (
        <Box className="loginMain__leftFrame__input__contents__errorText">
          The email address is in an invalid format.
        </Box>
      ) : (
        <></>
      )}
      {/* メールアドレス入力テキストボックス */}
      <ChkInputTextbox
        setValue={props.setEmailAddr}
        setError={setEmailAddrError}
        chkInputTxtError={format.BASIC_PATTERN_ERROR}
        chkLength={format.EMAIL_LENGTH_MAX}
        type="text"
        label={'E-mail address*'}
        value={props.emailAddr}
        id="mv-inputLoginUser"
        helperText=" "
        errHelperText="Incorrect entry."
        variant="standard"
        autoFocus={true}
        onKeyDown={event => {
          if (event.key === 'Enter' && reqPwdResetBtnDisabled === false) {
            event.preventDefault();
            onReqPwdReset();
          }
        }}
      />
      {/** REQUEST PASSWORD RESETボタン */}
      <Box className="loginMain__leftFrame__input__contents__button">
        {/* REQUEST PASSWORD RESETボタンは項目が入力されるまで白塗り */}
        {/* TextBoxにErrorが無い場合は、REQUEST PASSWORD RESETボタンを表示 */}
        {/* Errorが有る場合は、REQUEST PASSWORD RESETボタンをDisabled */}
        <PrimaryButton
          onClick={onReqPwdReset}
          buttonText={'REQUEST PASSWORD RESET'}
          id="mv-reqPasswordResetBtn"
          disabled={reqPwdResetBtnDisabled}
          className="loginMain__leftFrame__input__contents__button--resetEmail"
          apiWait={apiWait}
        />
      </Box>
      {/** BACKボタン */}
      <Box className="loginMain__leftFrame__input__contents__button">
        <PrimaryButton
          onClick={backButton}
          buttonText={'Back'}
          id="mv-resetPasswordBackBtn"
          className="loginMain__leftFrame__input__contents__button--back"
        />
      </Box>
    </Stack>
  );
};

/**
 * 検証コード送信後フォーム
 * @param props
 * - emailAddr メールアドレス
 * @returns JSX
 */
const AfterSendCodeForm = (props: {emailAddr: string}) => {
  const [newPassword, setNewPassword] = useState(''); //パスワード
  const [reEnterNewPassword, setReEnterNewPassword] = useState(''); //パスワード（2回目の入力）
  const [verificationCode, setVerificationCode] = useState(''); //検証コード
  const [resetPwdBtnDisabled, setResetPwdBtnDisabled] = useState(true); //RESET PASSWORDボタン有効フラグ
  const [verificationError, setVerificationError] = useState(''); //パスワードリセット時のエラー内容
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  //TextBoxのエラー状態
  const [passwordError, setPasswordError] = useState(false); //パスワード
  const [verificationCodeError, setVerificationCodeError] = useState(false); //検証コード

  //フォーマット正常フラグ
  const [passwordCheck, setPasswordCheck] = useState(true); //パスワード
  const [passMatchCheck, setPassMatchCheck] = useState(true); //パスワード（2回目の入力）

  /**
   * 検証コード送信後フォーム入力チェック
   * @returns 結果
   */
  const resetPwdFromDataCheck = () => {
    setPassMatchCheck(true);
    setPasswordCheck(true);
    //パスワード一致チェック
    if (newPassword !== reEnterNewPassword) {
      setPassMatchCheck(false);
      return false;
    }
    //パスワード形式チェック
    if (
      newPassword.search(/[A-Z]/) === -1 || // アルファベット（大文字）
      newPassword.search(/[a-z]/) === -1 || // アルファベット（小文字）
      newPassword.search(/[0-9]/) === -1 || // 数字
      newPassword.search(/[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]/) === -1 || // 記号
      newPassword.search(
        props.emailAddr.slice(0, props.emailAddr.indexOf('@'))
      ) !== -1 || // パスワードにユーザIDが含まれる
      newPassword.length < format.PASSWORD_LENGTH_MIN // 文字数 10文字以上
    ) {
      setPasswordCheck(false);
      return false;
    }
    return true;
  };

  /**
   * RESET PASSWORDボタン押下時処理
   */
  const onResetPassword = async () => {
    //パスワードの照合が成功していた場合にリセット処理
    //失敗の場合はエラーメッセージを表示
    setVerificationError('');
    setAPIWait(true);
    if (resetPwdFromDataCheck()) {
      //パスワードを新パスワードにリセットする
      await Auth.forgotPasswordSubmit(
        props.emailAddr,
        verificationCode,
        newPassword
      )
        .then(data => {
          alert('Reset Password Completed!');
          window.location.href = routeList.login.path;
        })
        .catch(err => {
          if (err.code === 'CodeMismatchException') {
            setVerificationError(
              'Invalid code provided, please request a code again.'
            );
          }
          if (err.code === 'LimitExceededException') {
            setVerificationError(
              'Attempt limit exceeded, please try after some time.'
            );
          }

          setAPIWait(false);
        });
    } else {
      setAPIWait(false);
    }
  };

  /**
   * RESET PASSWORDボタン出現処理
   * @returns JSX
   */
  const AddResetPasswordButton = () => {
    if (
      //TextBoxにデータが入力されているかチェック
      newPassword === '' ||
      reEnterNewPassword === '' ||
      verificationCode === '' ||
      //TextBoxの入力規則違反に対するエラー判定
      passwordError === true ||
      verificationCodeError === true ||
      //API処理中判定
      apiWait === true
    ) {
      setResetPwdBtnDisabled(true);
    } else {
      setResetPwdBtnDisabled(false);
    }

    //TextBoxにErrorが無い場合は、RESET PASSWORDボタンを表示
    //Errorが有る場合は、RESET PASSWORDボタンをDisabled
    return (
      <PrimaryButton
        onClick={onResetPassword}
        buttonText={'RESET PASSWORD'}
        id="mv-resetPasswordBtn"
        disabled={resetPwdBtnDisabled}
        className="loginMain__leftFrame__input__contents__button--resetPassword"
        apiWait={apiWait}
      />
    );
  };

  return (
    <Stack spacing={2} className="loginMain__leftFrame__input__contents">
      <h1>Reset your password</h1>
      <h2>Enter your new password and verification code.</h2>
      {/* 入力されたパスワードが一致しない場合は、警告文を表示 */}
      {passwordCheck === false ? (
        <Box className="loginMain__leftFrame__input__contents__errorText">
          The password is in an invalid format.
        </Box>
      ) : (
        <></>
      )}
      {passMatchCheck === false ? (
        <Box className="loginMain__leftFrame__input__contents__errorText">
          Passwords do not match. Please try again.
        </Box>
      ) : (
        <></>
      )}
      {/* 入力された検証コードが一致しない場合は、警告文を表示 */}
      {verificationError ? (
        <Box className="loginMain__leftFrame__input__contents__errorText">
          {verificationError}
        </Box>
      ) : (
        <></>
      )}
      {/* 新しいパスワード */}
      <ChkInputTextbox
        setValue={setNewPassword}
        setError={setPasswordError}
        chkInputTxtOK={format.PASSWORD_PATTERN_OK}
        chkLength={format.PASSWORD_LENGTH_MAX}
        type="password"
        label="New password*"
        value={newPassword}
        id="mv-loginPasswordInputBox"
        helperText={`The password must be at least ${format.PASSWORD_LENGTH_MIN} characters long including the following; At least one lowercase and uppercase character, a number and a symbol.It must not include any details of your email address.`}
        errHelperText="Incorrect entry."
        variant="standard"
        autoFocus={true}
      />
      {/* 確認用新しいパスワード */}
      <ChkInputTextbox
        setValue={setReEnterNewPassword}
        setError={setPasswordError}
        chkInputTxtOK={format.PASSWORD_PATTERN_OK}
        chkLength={format.PASSWORD_LENGTH_MAX}
        type="password"
        label="Confirm new password*"
        value={reEnterNewPassword}
        id="mv-loginPasswordInputBox"
        helperText=" "
        errHelperText="Incorrect entry."
        variant="standard"
      />
      {/* 検証コード */}
      <ChkInputTextbox
        setValue={setVerificationCode}
        setError={setVerificationCodeError}
        chkInputTxtOK={format.PASSWORD_PATTERN_OK}
        chkLength={format.PASSWORD_LENGTH_MAX}
        type="password"
        label="Verification code*"
        value={verificationCode}
        id="mv-loginPasswordInputBox"
        helperText="Enter the verification code sent by email"
        errHelperText="Incorrect entry."
        variant="standard"
      />
      {/** リセットボタン */}
      <Box className="loginMain__leftFrame__input__contents__button">
        {/* RESET PASSWORDボタンは項目が入力されるまで白塗り */}
        <AddResetPasswordButton />
      </Box>
    </Stack>
  );
};

export default ResetPassword;
